export interface LeaderboardEntry {
  name: string;
  score: number;
  flies: number;
  ts: number;
  equipped_character?: string | null;
  equipped_banner?: string | null;
  equipped_hat?: string | null;
  equipped_trail?: string | null;
  equipped_frame?: string | null;
  equipped_background?: string | null;
  equipped_background_night?: string | null;
  /** The player's clan name, shown as a tag on the leaderboard (null = no clan). */
  clan?: string | null;
  /** The clan's id — lets the tag open that clan. */
  clan_id?: number | null;
}

const API_BASE = "/api";

export type ClaimResult =
  | { kind: "ok"; token: string }
  | { kind: "taken" }
  | { kind: "invalid" }
  | { kind: "invalid_password" }
  | { kind: "blocked" }
  | { kind: "banned" }
  | { kind: "error" };

export type LoginResult =
  | { kind: "ok"; token: string; name: string }
  | { kind: "invalid" }
  | { kind: "banned" }
  | { kind: "error" };

export type RenameResult =
  | { kind: "ok"; name: string; token: string }
  | { kind: "taken" }
  | { kind: "invalid" }
  | { kind: "blocked" }
  | { kind: "forbidden" }
  | { kind: "not_found" }
  | { kind: "error" };

export class Leaderboard {
  private entries: LeaderboardEntry[] = [];

  /** Last fetched / submitted snapshot. Synchronous so renderers can read it on every frame. */
  all(): LeaderboardEntry[] {
    return this.entries.slice();
  }

  async refresh(): Promise<void> {
    try {
      const res = await fetch(`${API_BASE}/leaderboard`);
      if (!res.ok) return;
      const data = (await res.json()) as unknown;
      if (Array.isArray(data)) this.entries = data as LeaderboardEntry[];
    } catch {
      // network errors leave the previous cache in place
    }
  }

  /** Reserves a name globally and creates the account. */
  async claimName(name: string, password: string): Promise<ClaimResult> {
    try {
      const res = await fetch(`${API_BASE}/players`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, password }),
      });
      if (res.status === 201) {
        const data = (await res.json()) as { token?: string };
        if (typeof data.token === "string" && data.token.length > 0) {
          return { kind: "ok", token: data.token };
        }
        return { kind: "error" };
      }
      if (res.status === 409) return { kind: "taken" };
      if (res.status === 403) return { kind: "banned" };
      if (res.status === 400) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        if (data.error === "invalid_password") return { kind: "invalid_password" };
        if (data.error === "blocked_name") return { kind: "blocked" };
        return { kind: "invalid" };
      }
      return { kind: "error" };
    } catch {
      return { kind: "error" };
    }
  }

  /** Authenticates against an existing account. */
  async login(name: string, password: string): Promise<LoginResult> {
    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name, password }),
      });
      if (res.status === 200) {
        const data = (await res.json()) as { token?: string; name?: string };
        if (typeof data.token === "string" && typeof data.name === "string") {
          return { kind: "ok", token: data.token, name: data.name };
        }
        return { kind: "error" };
      }
      if (res.status === 403) return { kind: "banned" };
      if (res.status === 400 || res.status === 401) return { kind: "invalid" };
      return { kind: "error" };
    } catch {
      return { kind: "error" };
    }
  }

  /**
   * Migrate an account to a new (space-free) name, preserving all progress.
   * `token` authenticates a logged-in account; `password` is an alternative for
   * an account that has one but isn't currently logged in on this device.
   */
  async renameAccount(
    oldName: string,
    newName: string,
    token: string,
    password?: string,
  ): Promise<RenameResult> {
    try {
      const headers: Record<string, string> = { "content-type": "application/json" };
      if (token) headers.authorization = `Bearer ${token}`;
      const res = await fetch(`${API_BASE}/players/rename`, {
        method: "POST",
        headers,
        body: JSON.stringify(
          password ? { name: oldName, newName, password } : { name: oldName, newName },
        ),
      });
      if (res.status === 200) {
        const data = (await res.json()) as { name?: string; token?: string };
        if (typeof data.name === "string") {
          return { kind: "ok", name: data.name, token: typeof data.token === "string" ? data.token : "" };
        }
        return { kind: "error" };
      }
      if (res.status === 409) return { kind: "taken" };
      if (res.status === 403) return { kind: "forbidden" };
      if (res.status === 404) return { kind: "not_found" };
      if (res.status === 400) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        return data.error === "blocked_name" ? { kind: "blocked" } : { kind: "invalid" };
      }
      return { kind: "error" };
    } catch {
      return { kind: "error" };
    }
  }

  /** Checks whether a name is already taken globally. */
  async isNameAvailable(name: string): Promise<boolean> {
    try {
      const res = await fetch(`${API_BASE}/players/${encodeURIComponent(name)}`);
      if (!res.ok) return false;
      const data = (await res.json()) as { exists?: boolean };
      return data.exists === false;
    } catch {
      return false;
    }
  }

  async resetLeaderboard(adminName: string): Promise<boolean> {
    try {
      const res = await fetch(`${API_BASE}/admin/reset-leaderboard`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: adminName }),
      });
      if (!res.ok) return false;
      this.entries = [];
      return true;
    } catch {
      return false;
    }
  }

  async resetPlayer(adminName: string, targetName: string): Promise<boolean> {
    try {
      const res = await fetch(`${API_BASE}/admin/reset-player`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: adminName, target: targetName }),
      });
      if (!res.ok) return false;
      await this.refresh();
      return true;
    } catch {
      return false;
    }
  }

  async submit(
    name: string,
    token: string,
    score: number,
    flies: number,
  ): Promise<{ rank: number | null; unlocked: string[] }> {
    if (!name || !token) return { rank: null, unlocked: [] };
    try {
      const res = await fetch(`${API_BASE}/runs`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name, score, flies }),
      });
      if (!res.ok) return { rank: null, unlocked: [] };
      const data = (await res.json()) as {
        rank?: number | null;
        entries?: LeaderboardEntry[];
        unlocked?: string[];
      };
      if (Array.isArray(data.entries)) this.entries = data.entries;
      return { rank: data.rank ?? null, unlocked: Array.isArray(data.unlocked) ? data.unlocked : [] };
    } catch {
      return { rank: null, unlocked: [] };
    }
  }
}
